<template>
	<div class="role-selection">
		<v-progress-circular v-if="isLoading"></v-progress-circular>
		<div v-else>
			<h2 class="role-selection__title">Select your Role</h2>
			<div
				class="role-selection__card"
				:class="{ 'role-selection__card--checked': selectedRole === role.slug }"
				v-for="role in roles"
				:key="role.user_category_id"
				@click="selectRole(role.slug)"
			>
				<div class="role-selection__icon mr-2">
					<Icon :type="role.icon" color="var(--accent-color)" :size="20" />
				</div>
				<div class="role-selection__content">
					<h3 class="role-selection__name">{{ role.user_category_title }}</h3>
					<p class="role-selection__description">{{ role.user_category_description }}</p>
				</div>
				<CustomCheckbox
					class="role-selection__card__checkbox"
					v-if="selectedRole === role.slug"
					:isChecked="selectedRole === role.slug"
				/>
			</div>
			<div class="role-selection__button-container">
				<v-btn
					v-if="selectedRole"
					@click="navigateToRegisterPage"
					:class="{ visible: selectedRole }"
					class="role-selection__button"
				>
					Continue
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import CustomCheckbox from '@/components/CustomCheckbox.vue'
import Form from '@/mixins/Form'
import { mapGetters, mapActions } from 'vuex'
import TokenService from '@/services/token.service'
import LocalStorageService from '@/services/local-storage.service'

export default {
	name: 'RoleSelection',
	mixins: [Form],
	components: { CustomCheckbox },
	computed: {
		...mapGetters('auth', ['UserLogInCategories', 'userRegistrationCategories', 'isLoading']),
		...mapGetters('settings', ['login']),
		roles() {
			if (this.source === 'login') {
				return this.UserLogInCategories
			} else if (this.source === 'register') {
				return this.userRegistrationCategories
			}
			return []
		},
		hostName() {
			return window.location.hostname || 'localhost'
		},
		protocol() {
			return window.location.protocol || 'http'
		},
		deviceId() {
			return TokenService.getDeviceIdFromStorage()
		},
		externalLoginUrl() {
			return `${this.protocol}//${this.hostName}/oauth?device-id=${this.deviceId}&redirect_url=${this.protocol}//${this.hostName}${process.env.VUE_APP_PUBLIC_PATH}oauth2-callback`
		},
	},
	data() {
		return {
			selectedRole: null,
			source: this.$route.query.source || 'unknown',
		}
	},

	created() {
		if (this.source === 'login' && this.UserLogInCategories.length === 0) {
			this.getLogInCategory()
		} else if (this.source === 'register' && this.userRegistrationCategories.length === 0) {
			this.getRegisterCategories()
		}
	},
	methods: {
		...mapActions('auth', ['getLogInCategory', 'getRegisterCategories', 'clearCategories']),
    additionalInfo(settings){
      const {
        renew_url,
        renew_button_name,
        renew_area_text,
        privacy_policy,
        terms_of_use,
      } = settings || {}

      const payload = {
        renew_url: renew_url || '',
        renew_button_name: renew_button_name || '',
        renew_area_text: renew_area_text || '',
        privacy_policy: privacy_policy || '',
        terms_of_use: terms_of_use || '',
      }
      LocalStorageService.set('additionalLoginSettings', JSON.stringify(payload))
    },
		selectRole(roleId) {
			this.selectedRole = this.selectedRole === roleId ? null : roleId
		},
		isDeepLink(link = '') {
			if (!link) {
				return false
			}
			return !link.startsWith('http')
		},
		navigateToRegisterPage() {
			if (!this.selectedRole) return

			const selectedRoleObj = this.roles.find((role) => role.slug === this.selectedRole)
			if (!selectedRoleObj) return

			const { slug, integration, user_category_id, registration_settings, login_settings } =
				selectedRoleObj
			const redirectToNonIntegratedUrl = (url, queryParams = {}) => {
				if (!url) return
				if (this.isDeepLink(url)) {
					const cleanedUrl = url.replace(/^[^:]+:\/\/\/*/, '')
					const queryString = Object.entries(queryParams)
						.map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
						.join('&')

					window.location.href = `${window.location.origin}/webapp/auth/${cleanedUrl}?${queryString}`
				} else {
					const queryString = Object.entries(queryParams)
						.map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
						.join('&')

					window.location.href = `${url}?${queryString}`
				}
			}

			if (this.source === 'register') {
				if (
					slug === 'user' &&
					registration_settings?.external_registration === 1 &&
					registration_settings?.registration_url
				) {
					window.location.href = registration_settings.registration_url
					return
				}

				if (integration === 'non-integrated') {
					redirectToNonIntegratedUrl(registration_settings?.registration_url, {
						slug,
						user_category_id,
					})
					return
				}

				this.$router.push({
					name: 'registration',
					query: {
						slug,
						user_category_id,
					},
				})
				return
			}
			if (this.source === 'login') {
				if (login_settings?.external_login === 1) {
          this.additionalInfo(selectedRoleObj.additional_settings)
					window.location.href = `${
						this.externalLoginUrl
					}&user_category_id=${encodeURIComponent(user_category_id)}`
          return
				}
				if (integration === 'non-integrated') {
          this.additionalInfo(selectedRoleObj.additional_settings)
					redirectToNonIntegratedUrl(login_settings?.login_url, {
						slug,
						user_category_id,
					})
					return
				}
        this.additionalInfo(selectedRoleObj.additional_settings)
				this.$router.push({
					name: 'login',
					query: {
						slug,
						user_category_id,
					},
				})
			}
		},
	},
	beforeDestroy() {
		this.clearCategories()
	},
}
</script>

<style lang="scss">
.role-selection {
	max-width: 555px;
	width: 100%;
	padding: 12px;
	margin-left: auto;
	margin-right: auto;

	&__title {
		display: flex;
		align-items: flex-start;
		width: 100%;
		max-width: 480px;
		justify-content: flex-start;
		height: 40px;
		margin: 10px auto;
		font-family: 'Montserrat', sans-serif;
		font-weight: 700;
		font-size: 36px;
		line-height: 43.88px;
		color: #4a4a4a;
	}

	&__card {
		display: flex;
		align-items: flex-start;
		width: 100%;
		max-width: 480px;
		justify-content: flex-start;
		min-height: 96px;
		height: 100%;
		border-radius: 10px;
		padding: 15px;
		margin: 10px auto;
		border: 1px solid #e5e5e5;
		cursor: pointer;
		position: relative;
		&__checkbox {
			position: absolute;
			top: 15px;
			right: 5px;
		}
	}

	&__icon img {
		margin-left: 5px;
		align-items: center;
		width: 40px;
		height: 40px;
		margin-right: 15px;
	}

	&__content {
		text-align: left;
		flex: 1;
	}

	&__name {
		font-family: 'Montserrat', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 21.94px;
		color: #002855;
	}

	&__description {
		font-family: 'Montserrat', sans-serif;
		font-weight: 400;
		font-size: 14px;
		line-height: 17.07px;
		margin-bottom: 12px;
		color: #4a4a4a;
	}

	&__card--checked {
		border: 1.5px solid #0055a3;
	}

	&__button-container {
		height: 70px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__button {
		margin-top: 20px;
		width: 320px;
		height: 50px;
		border-radius: 8px;
		padding: 14px 44px;
		background-color: #002855 !important;
		font-family: 'Montserrat', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 21.94px;
		text-align: center !important;
		color: #ffffff !important;
	}
}
</style>
